// it's necessary for tracking GA
import { OrganizationSignupDetails } from '../types/enterprise';

export const navigateWithReload = (route: string) => {
  window.location.href = `${window.location.origin}${route}`;
};

export const navigateToExternalRoute = (route: string) => {
  window.location.href = route;
};

export const validateUrl = (url: string | null) => {
  if (url?.includes(window.location.origin)) {
    return url;
  }
  return null;
};

export const getOrgDetailsFromQueryString = (
  searchParams: URLSearchParams,
): OrganizationSignupDetails | null => {
  const organizationId = Number(searchParams.get('org_id'));
  const organizationToken = searchParams.get('org_token');
  if (organizationId && organizationToken) {
    return {
      id: organizationId,
      token: decodeURIComponent(organizationToken),
    };
  }
  return null;
};

export const getOrgParamsStatus = (searchParams: URLSearchParams) => {
  const hasOrgId = searchParams.has('org_id');
  const hasOrgToken = searchParams.has('org_token');
  const orgId = searchParams.get('org_id') || '';
  const orgToken = searchParams.get('org_token') || '';

  return {
    paramsPresent: hasOrgId && hasOrgToken,
    anyEmpty: (hasOrgId && orgId === '') || (hasOrgToken && orgToken === ''),
  };
};

export const getLocalStorageItem = (key: string) => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : value;
};

export const cleanLocalStorageItems = (items: Array<string>): void => {
  items?.forEach(item => localStorage.removeItem(item));
};

export const initializeFontLoading = async (): Promise<void> => {
  try {
    if (document?.fonts?.load) {
      await document.fonts.load('1em Value');
      await document.fonts.load('1em Vanitas');
    }
  } catch {
    console.warn('Font loading failed, falling back to system fonts');
  } finally {
    document.body.classList.add('fonts-loaded');
  }
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Attempts to redirect to mobile app via deeplink with fallback to web
 * @param deeplink - The deeplink URL for the mobile app
 * @param fallbackUrl - The web URL to fallback to if app is not installed
 * @param timeout - Timeout in milliseconds before fallback (default: 2500)
 */
export const redirectToAppWithFallback = (
  deeplink: string,
  fallbackUrl?: string,
  timeout = 2500,
): void => {
  // Create a hidden iframe to attempt the deeplink
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = deeplink;
  document.body.appendChild(iframe);

  // Set up fallback timer only if fallbackUrl is provided
  const fallbackTimer = fallbackUrl
    ? setTimeout(() => {
        document.body.removeChild(iframe);
        window.location.href = fallbackUrl;
      }, timeout)
    : null;

  // Listen for page visibility change (indicates app opened)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      document.body.removeChild(iframe);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Also try direct window.location as backup
  setTimeout(() => {
    try {
      window.location.href = deeplink;
    } catch (e) {
      // If deeplink fails and no fallback, just clean up
      if (!fallbackUrl) {
        document.body.removeChild(iframe);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    }
  }, 100);
};

export const formatFirstName = (name: string) => {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .split(' ')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};
